import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { resolveEquipmentPayload } from 'src/utils/quotes';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormStepper from 'src/components/FormStepper/FormStepper';
import DialogTransition from 'src/components/DialogTransition';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { CREATE_CONTRACT_FROM_QUOTE } from 'src/store/actions/contractActions';
import CurrentStep from './CurrentStep';
import useStyles from './styles';
import makeInitialState from './initialState';

const baseStepperLabels = [
  'COMMON.QUOTE',
  'COMMON.CLIENT',
  'COMMON.AVALS',
  'COMMON.ADDRESS',
  'COMMON.PAYMENT_METHOD',
  'COMMON.CONFIRMATION'
];

const SendQuoteToContractForm = ({
  open,
  handleClose,
  onSubmit,
  term,
  initialPayment,
  initialPaymentDate,
  firstRentDate,
  gracePeriod,
  client,
  equipmentsList,
  includeEquipmentStep,
  isFirstUpfrontRentModality,
  residual
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const initialState = makeInitialState({
    term,
    initialPayment,
    client,
    initialPaymentDate,
    firstRentDate,
    gracePeriod,
    equipmentsList,
    isFirstUpfrontRentModality,
    residual
  });
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialState);
  const stepperLabels = includeEquipmentStep
    ? [
        'COMMON.QUOTE',
        'COMMON.CLIENT',
        'COMMON.EQUIPMENT',
        ...baseStepperLabels.slice(2)
      ]
    : baseStepperLabels;
  const isLoading = useSelector(
    isLoadingSelector([CREATE_CONTRACT_FROM_QUOTE.action])
  );

  const resetRef = useRef();
  useEffect(() => {
    setData(initialState);
    return () => {
      setStep(0);
    };
  }, [open]);

  const handleNext = useCallback(
    values => {
      setData({ ...data, ...values });
      setStep(step + 1);
    },
    [setStep, step, setData, data]
  );

  const handleCloseDialog = () => {
    handleClose();
    setData(initialState);
  };

  const handleBackOrClose = useCallback(() => {
    const paymentMethodStep = includeEquipmentStep ? 5 : 4;
    const avalsStep = includeEquipmentStep ? 3 : 2;
    const addressStep = includeEquipmentStep ? 4 : 3;

    if (step === 0) {
      handleCloseDialog();
      setData(initialState);
    } else if (step === paymentMethodStep) {
      if (data.isExistingAddress) {
        setStep(avalsStep);
      } else {
        setStep(addressStep);
      }
    } else {
      setStep(step - 1);
    }
  }, [dispatch, step, setStep, handleCloseDialog, includeEquipmentStep]);

  const handleSubmit = useCallback(
    value => {
      const mergedValues = { ...data, ...value };
      const { equipment, equipments, ...payload } = mergedValues;
      const isMultiEquipment = data.equipmentsList?.length > 1;

      if (!includeEquipmentStep) {
        onSubmit(payload);
        setData(initialState);
        return;
      }

      if (isMultiEquipment && equipments?.length) {
        const equipmentsListPayload = equipments.map((item, index) =>
          resolveEquipmentPayload(item, data.equipmentsList?.[index])
        );
        onSubmit({ ...payload, equipmentsList: equipmentsListPayload });
      } else if (equipment) {
        onSubmit({
          ...payload,
          equipment: resolveEquipmentPayload(
            equipment,
            data.equipmentsList?.[0]
          )
        });
      } else {
        onSubmit(payload);
      }

      setData(initialState);
    },
    [data, initialState, includeEquipmentStep, onSubmit]
  );

  if (!open) return null;

  return (
    <Box>
      <Dialog
        aria-labelledby="fsend-quote-to-contract-dialog"
        classes={{ paper: classes.root }}
        open={open}
        scroll="body"
        TransitionComponent={DialogTransition}
        keepMounted
        disableEscapeKeyDown
      >
        <DialogTitle
          className={classes.title}
          id="send-quote-to-contract-title"
        >
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={() => {
              handleCloseDialog();
            }}
            disabled={isLoading}
            className={classes.closeIcon}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.title} color="primary" variant="h2">
            {t('ENUMS.QUOTE_ACTIONS.SEND_TO_CONTRACT')}
          </Typography>
        </DialogTitle>
        <Divider light variant="middle" />
        <DialogContent
          sx={{
            height: 'auto',
            maxHeight: 'calc(100vh - 200px)'
          }}
        >
          <FormStepper index={step} labels={stepperLabels} />
          <CurrentStep
            open={open}
            activeStep={step}
            data={data}
            includeEquipmentStep={includeEquipmentStep}
            resetRef={resetRef}
            onNext={handleNext}
            onBack={handleBackOrClose}
            onSubmit={handleSubmit}
            isQuoteAssignedToClient={Boolean(data.isQuoteAssignedToClient)}
            clientAddress={{
              street: client.street,
              externalHomeNumber: client.externalHomeNumber,
              insideHomeNumber: client.insideHomeNumber,
              zipCode: client.zipCode,
              city: client.city,
              colonia: client.colonia,
              state: client.state
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

SendQuoteToContractForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  term: PropTypes.string,
  initialPayment: PropTypes.number,
  client: PropTypes.object,
  initialPaymentDate: PropTypes.string,
  firstRentDate: PropTypes.string,
  gracePeriod: PropTypes.number,
  equipmentsList: PropTypes.array,
  includeEquipmentStep: PropTypes.bool,
  isFirstUpfrontRentModality: PropTypes.bool,
  residual: PropTypes.number
};

SendQuoteToContractForm.defaultProps = {
  includeEquipmentStep: false
};

export default SendQuoteToContractForm;
